import React, { useState, useEffect, useContext } from 'react';
import Helmet from 'react-helmet';
import FeaturesSlider from '@/components/FeaturesSlider/FeaturesSlideV2';
import { getUrlFromEnv } from '@/utils/getUrlFromEnvUtil';
import { redirectUrl } from '@/components/EstimateRateWidget/utils';
import Faq from '@/components/Faq/FaqV2/Faq';
import { ImportantInformation } from '@latitude/important-information-rebrand';
import Layout from '@/components/layout';
import MobileAppInstallPrompts from '@latitude/mobile-app-install-prompt/MobileAppInstallPrompt';
import PageData from '@/data/pages/debt-consolidation-loans.json';
import PLData from '@/data/pages/personal-loan.json';
import StickyNavigation from '@/components/StickyNavigation/StickyNavigationV2';
import { Box } from '@/components/Box/Box';
import {
  BREAKPOINT,
  COLOR,
  SITE_URL,
  SOFT_QUOTE_LOANS_AU_URL,
  PL_APPLY_CTA_TRACK_ID
} from '@/utils/constants';
import { graphql, useStaticQuery } from 'gatsby';
import ApplyEligibility from '@/components/ApplyEligibility/ApplyEligibility';
import footerData from '@/data/json/footer.json';
import styled from 'styled-components';
import Button from '../components/Button/Button';
import { PageContext } from '@/context/PageContext';
import Section from '@latitude/section-rebrand';
import ProductComparisonSection from '@/components/PersonalLoanPageContent/ProductComparisionSectionRebrand';
import PersonalLoanCalculatorV2 from '@/components/PersonalLoanCalculatorRebrandV2/PersonalLoanCalculator';
import CardCollection from '@/components/lab-components/CardCollection/CardCollectionV2';
import ProofPointCollection from '@/components/lab-components/ProofPointCollection/ProofPointCollectionV2';
import { REBRAND_COLOR, PADDING } from '@latitude/core/utils/constants';
import Typography from '@latitude/typography';
import HeroV2 from '@/components/Hero/HeroV2/HeroV2';
import PromoBanner from '@/components/lab-components/PromoBanner/PromoBannerV2';

const CTA_BUTTON_LABEL = 'Start here';

const DebtConsolidationLoanPage = ({ location }) => {
  const applyUrl = `${getUrlFromEnv(
    'funnel-start-loans'
  )}${SOFT_QUOTE_LOANS_AU_URL}`;

  // soft quote
  const [softQuoteHref, setSoftQuoteHref] = useState();

  /** Contentful Data */
  const contentfulPageData = useContext(PageContext);
  const state = contentfulPageData?.updatedBody || {};

  useEffect(() => {
    sessionStorage.setItem('purpose', 'debt-consolidation'); // reset session val used in `EstimateRateWidgetSection`
    setSoftQuoteHref(redirectUrl('au', true));
  }, []);

  const pageQuery = useStaticQuery(graphql`
    {
      allMarkdownRemark(
        filter: {
          fileAbsolutePath: {
            glob: "**/src/templates/life-done-better/articles/*.md"
          }
          frontmatter: {
            title: {
              in: [
                "Debt Consolidation: What You Need to Know"
                "Debt Consolidation: What You Should Consider"
                "Is a Debt Consolidation Loan Right for You?"
              ]
            }
          }
        }
      ) {
        edges {
          node {
            html
            frontmatter {
              title
              path
              thumbnail
            }
          }
        }
      }
    }
  `);

  function transformData(data) {
    return {
      title: {
        nodeType: 'document',
        data: {},
        content: [
          {
            nodeType: 'heading-3',
            data: {},
            content: [
              {
                nodeType: 'text',
                value: 'Recommended reads',
                marks: [],
                data: {}
              }
            ]
          },
          {
            nodeType: 'paragraph',
            data: {},
            content: [
              {
                nodeType: 'text',
                value: '',
                marks: [],
                data: {}
              }
            ]
          }
        ]
      },
      cards: data?.map(item => ({
        image: {
          file: {
            url: `/life-done-better/images/${item?.node?.frontmatter?.thumbnail}`
          }
        },
        title: {
          data: {},
          content: [
            {
              data: {},
              content: [
                {
                  data: {},
                  marks: [],
                  value: item?.node?.frontmatter?.title,
                  nodeType: 'text'
                }
              ],
              nodeType: 'paragraph'
            }
          ],
          nodeType: 'document'
        },
        tileUrl: item?.node?.frontmatter?.path
      }))
    };
  }

  return (
    <Layout location={location} customFooter={footerData} hasStickyNav>
      <main className="navigation-spacer">
        <Box backgroundColor={COLOR.GREY6}>
          <MobileAppInstallPrompts />
          <Helmet>
            <link rel="canonical" href={`${SITE_URL}${location.pathname}`} />
            <title>{PageData.metaTitle}</title>
            <meta name="description" content={PageData.metaDesc} />
          </Helmet>
          <HeroV2
            {...(state?.heroBannerData?.[0] ||
              PLData?.contentful?.heroBannerDataDebt?.[0])}
          />

          <StickyNavigation
            items={PLData?.contentful?.inPageNavData.items}
            phoneNumber={PLData.content.phoneNumber}
            ctaHref={applyUrl}
            ctaText={CTA_BUTTON_LABEL}
            trackEventData={{
              category: 'cta',
              action: 'quote-link',
              location: 'sticky'
            }}
            trackId="sticky-get-started"
            offsetElem="[data-sticky-navigation-offset]"
            {...state?.inPageNavData?.[0]}
          />
          <FeaturesSlider
            key={
              (
                state?.featureSliderData?.[0] ||
                PLData?.contentful?.featureSliderData[0]
              ).heading
            }
            heading={
              (
                state?.featureSliderData?.[0] ||
                PLData?.contentful?.featureSliderData[0]
              ).heading
            }
            id="why-debt-con"
            className="why-choose"
            data={
              (
                state?.featureSliderData?.[0] ||
                PLData?.contentful?.featureSliderData[0]
              ).featureCards
            }
            backgroundColor={
              state?.featureSliderData?.[0]?.backgroundColor?.value ||
              REBRAND_COLOR.WHITE
            }
          />
          <section
            id="repayment-calculator"
            css={`
              padding: 0px !important;
            `}
          >
            <div
              id="repayment-calculator"
              css={`
                background-color: ${REBRAND_COLOR.WHITE};
                padding-top: 32px;
                padding-bottom: 32px;

                && .typography-base__sh1 {
                  margin-bottom: 30px !important;
                }
              `}
            >
              <Section>
                <Typography.SH1 color={REBRAND_COLOR.BLACK} semibold>
                  Get an estimate of your repayments
                </Typography.SH1>

                <PersonalLoanCalculatorV2
                  loans1
                  ctaButtonLabel="Get your personalised rate"
                  applyCTATrackId={PL_APPLY_CTA_TRACK_ID}
                />
              </Section>
            </div>
          </section>
          <PromoBanner
            {...(state?.promoBannerData?.[0] ||
              PLData?.contentful?.promoBannerData?.[0])}
          />
          <ApplyEligibility
            title="How the debt consolidation process works"
            data={PageData.content.loanProcess.list}
            id="how-it-works"
          />

          <ProductComparisonSection
            id="compare-products"
            {...state?.productComparisonData?.[0]}
          />

          <ProofPointCollection
            id="calculators-and-tools"
            data={
              state?.proofPointCollectionData?.[0] ||
              PLData?.contentful?.proofPointCollectionData?.[0]
            }
          />

          <CardCollection
            id="applying-is-simple"
            data={
              state?.cardCollectionData?.[0] ||
              PLData?.contentful?.cardCollectionData?.[0]
            }
          />

          <CardCollection
            id="Recommended reads"
            data={
              state?.cardCollectionData?.[1] ||
              transformData(pageQuery?.allMarkdownRemark?.edges)
            }
            category="text-link"
          />
          {state?.faqData?.[0] && <Faq {...state?.faqData?.[0]} />}
          <ImportantInformation
            data={require('@/data/json/disclaimer/personal-loan1.json')}
            css={`
              background-color: ${REBRAND_COLOR.STONE};
              overflow-x: hidden;
              .row {
                @media (min-width: 1600px) {
                  padding-left: ${PADDING.P32};
                  padding-right: ${PADDING.P32};
                }
              }
            `}
            {...{
              ...state?.importantInfoData?.[0],
              sectionOneColummClasses: 'col-12 col-lg-6',
              sectionTwoColummClasses: 'col-12 col-lg-6'
            }}
          />
        </Box>
      </main>
    </Layout>
  );
};

export default DebtConsolidationLoanPage;
